import { useEffect, useRef, useState } from 'react';
import { MapPinPen } from 'lucide-react';
import toast from 'react-hot-toast';
import profileController from '@/services/ProfileController';
import useDebounce from '@/utils/useDebounce';
import useWindowSize from '@/utils/useWindowSize';
import Autocomplete from './autocomplete/Autocomplete';
import LiveMap from './orders/new-order/live-map/LiveMap';
import { FloatingTextinput } from '@/ui/floating-textinput/floating-textinput';
import Button from '@/ui/button/Button';
import colors from 'tailwindcss/colors';
import { MapPinPlus } from 'lucide-react';

export const AddressWrapper = ({
	user,
	label,
	address,
	setAddress,
	errorAddress,
	setErrorAddress,
	onSelectSuggestion,
	showDropper = true,
	// Every other caller edits an address that has to exist (a shop, a city, a
	// client address row). The new-order form is the exception: the shop can
	// have the field on as merely optional, and then an empty one is a valid
	// answer — only a half-typed street with no location is an error.
	optional = false,
	onFocus,
	// A client's address, picked by a shop in a city whose areas come from the
	// pin («Αυτόματα»): a point outside every area is refused on the spot, the
	// same answer the order itself would get when sent.
	withinServiceArea = false,
}) => {
	const [mapAddress, setMapAddress] = useState('');

	const [pin, setPin] = useState(null);
	const [tempMapLocation, setTempMapLocation] = useState(null);
	const [isMapOpen, setIsMapOpen] = useState(false);
	const [errorMapAddress, setErrorMapAddress] = useState(false);

	const [suggestions, setSuggestions] = useState([]);
	const debouncedClientSearchText = useDebounce(address?.street, 800);

	// const [disableSug, setDisableSug] = useState(true);

	const disableSugRef = useRef(true);

	const { width: screenWidth } = useWindowSize();

	const getSuggestions = async input => {
		console.log('getSuggestions');
		if (disableSugRef.current) {
			return;
		}
		try {
			const res = await profileController.getAutocomplete(input);
			// `?? []` because the endpoint answers an upstream Google failure with
			// { error, code: 509 } and no predictions at all — assigning that
			// undefined would blow up the .map() that renders the dropdown.
			setSuggestions(res?.predictions ?? []);
		} catch (err) {
			console.log('get suggestions errr', err);
		}
	};

	// Backstop only. What actually arms lookups is the user typing (see
	// onChangeText): the guard exists so the value the field is *mounted* with —
	// a shop's saved address, a client's address row — does not fire a paid
	// lookup and pop the dropdown open on its own, and so the street we write
	// back after a suggestion is picked does not immediately re-query.
	//
	// Arming on a wall-clock timer alone was safe while every caller was a modal
	// the user opens seconds before reaching the address field. The order form
	// mounts this at the moment of use, so a first query could land inside the
	// window and be dropped with nothing on screen to say so.
	useEffect(() => {
		const id = setTimeout(() => {
			// setDisableSug(false);
			disableSugRef.current = false;
		}, 1200);

		return () => clearTimeout(id);
	}, []);

	useEffect(() => {
		if (!(debouncedClientSearchText?.length > 2)) {
			return;
		}

		getSuggestions(debouncedClientSearchText);
	}, [debouncedClientSearchText]);

	// true when the point may be used. Only a shop is held to the areas — an
	// admin can still place an order anywhere and pick its area by hand.
	const isInsideServiceArea = async location => {
		if (
			!withinServiceArea ||
			user?.role !== 'shop' ||
			user?.cityLocation?.areaMode !== 'auto' ||
			!location?.lat
		) {
			return true;
		}
		const res = await profileController.getAreaForLocation(
			location.lat,
			location.lng,
			user?.cityLocation?.id,
		);
		if (res?.mode === 'auto' && res?.area === '') {
			toast.error('Το στίγμα είναι εκτός περιοχών εξυπηρέτησης');
			return false;
		}
		return true;
	};

	// console.log('----- > DISABLED', disableSugRef.current);
	const getResults = async (prediction, placeId) => {
		try {
			// console.log(prediction);
			const res = await profileController.geocodeByAddress(placeId);
			// console.log(res);
			if (!res?.results?.length) {
				return;
			}
			if (!(await isInsideServiceArea(res.results[0]))) {
				setSuggestions([]);
				setErrorAddress(1);
				return;
			}
			// removeGarbageFromLocation(location);

			const streetParts = prediction.split(',');
			// console.log("streetParts", streetParts);
			let str = '';
			for (let i = 0; i < streetParts.length - 1; i++) {
				str += streetParts[i];
				if (i !== streetParts.length - 2) {
					str += ', ';
				}
			}

			// setStreet(str);

			// setLocation(res?.results?.[0]);

			setAddress({
				...address,
				street: str,
				location: res?.results?.[0],
			});

			if (typeof onSelectSuggestion === 'function') {
				onSelectSuggestion({
					...address,
					street: str,
					location: res?.results?.[0],
				});
			}
			// setErrorName(false);

			setErrorAddress(false);
			setSuggestions([]);
			disableSugRef.current = true;
			setTimeout(() => {
				disableSugRef.current = false;
			}, 1_000);
		} catch (err) {
			console.log('get results errr', err);
		}
	};

	// const onSelectSuggestion = value => {
	// 	console.log('onSelectSuggestion');
	// 	let location;
	// 	geocodeByAddress(value)
	// 		.then(async results => {
	// 			console.log('results', results);
	// 			location = results[0];

	// 			removeGarbageFromLocation(location);

	// 			const streetParts = value.split(',');
	// 			// console.log("streetParts", streetParts);
	// 			let str = '';
	// 			for (let i = 0; i < streetParts.length; i++) {
	// 				//   console.log("streetParts[i]"), streetParts[i];
	// 				str += streetParts[i];
	// 				if (i !== streetParts.length - 1) {
	// 					str += ', ';
	// 				}
	// 			}

	// 			// console.log('streetParts', streetParts);
	// 			// setValue('name', str || value);
	// 			const latLng = await getLatLng(results[0]);
	// 			location.latLng = latLng;

	// 			// setErrorName(false);

	// 			handleChange({ location, street: str });
	// 		})
	// 		.catch(error => console.error('Error', error));
	// };

	// const onChangeSuggestion = value => {
	// 	// setValue('name', value);
	// 	// setAddressText('value');
	// 	console.log('onChangeSuggestion');

	// 	handleChange({ location: null, street: value });
	// };

	// const handleChange = ({ location, street }) => {
	// 	const loc = {
	// 		lat:
	// 			typeof location?.geometry?.location?.lat === 'function'
	// 				? location?.geometry?.location?.lat()
	// 				: location?.geometry?.location?.lat || location?.lat,
	// 		lng:
	// 			typeof location?.geometry?.location?.lng === 'function'
	// 				? location?.geometry?.location?.lng()
	// 				: location?.geometry?.location?.lng || location?.lng,
	// 	};

	// 	setAddress({
	// 		street,
	// 		location: loc,
	// 	});
	// };

	// const validateMapAddress = () =>
	// 	// console.log('should err: ', !mapAddress, mapAddress, tempMapLocation) ||
	// 	setErrorMapAddress(!mapAddress || !tempMapLocation?.lat);
	// setErrorMapAddress(!mapAddress);

	const validateAddress = () => {
		// console.log('qqq validateAddress', address);

		if (optional && !address?.street?.length) {
			setErrorAddress(0);
			return 0;
		}

		const error = address?.street?.length > 0 && address?.location?.lat ? 0 : 1;
		setErrorAddress(error);
		return error;
		// setErrorMapAddress(!mapAddress);
	};

	const validateMapAddress = () => {
		console.log('validateMapAddress', mapAddress, tempMapLocation);
		const error = mapAddress?.length && tempMapLocation?.lat ? 0 : 1;
		setErrorMapAddress(error);
		return error;
		// setErrorMapAddress(!mapAddress);
	};

	// console.log('qqq er', errorAddress);
	// console.log('qqq error', errorAddress);

	// console.log('GOOD LOGGER ', mapAddress, tempMapLocation);
	const openMap = () => {
		// console.log("open map location ", location);
		const { location, street } = address || {};

		if (!location || !street) {
			setIsMapOpen(true);
			return;
		}

		const lat =
			typeof location?.geometry?.location?.lat === 'function'
				? location?.geometry?.location?.lat()
				: location?.geometry?.location?.lat || location?.lat;

		const lng =
			typeof location?.geometry?.location?.lng === 'function'
				? location?.geometry?.location?.lng()
				: location?.geometry?.location?.lng || location?.lng;

		// console.log('xx', location);
		setTempMapLocation({ lat, lng });

		setPin({
			dummy: {
				id: -2,
				name: 'Επιλεγμένη τοποθεσία',
				coords: {
					latitude: lat,
					longitude: lng,
				},
			},
		});

		setMapAddress(street);
		setIsMapOpen(true);
	};

	const onBack = () => {
		// setDisableSug(true);
		disableSugRef.current = true;
		setTimeout(() => {
			// setDisableSug(false);
			disableSugRef.current = false;
		}, 1_000);
		setTempMapLocation(null);
		setMapAddress('');
		setPin(null);
		// setErrorMapAddress(undefined);
		setIsMapOpen(false);
	};

	const onSaveMap = async () => {
		if (!(await isInsideServiceArea(tempMapLocation))) {
			return;
		}
		// setDisableSug(true);
		disableSugRef.current = true;

		setTimeout(() => {
			// setDisableSug(false);
			disableSugRef.current = false;
		}, 1_000);
		setAddress({
			...address,
			street: mapAddress,
			location: tempMapLocation,
		});
		setTempMapLocation(null);
		setMapAddress('');
		setPin(null);
		setIsMapOpen(false);
	};

	const changePin = (lat, lng) => {
		const location = {
			lat,
			lng,
		};
		setPin({
			dummy: {
				id: -2,
				name: 'Επιλεγμένη τοποθεσία',
				coords: {
					latitude: lat,
					longitude: lng,
				},
			},
		});
		setTempMapLocation(location);
		// TODO den kanei rerender epeidi ref mallon
		setErrorMapAddress(!mapAddress);
	};

	const saveDisabledMap = () => {
		return errorMapAddress || errorMapAddress === undefined || !pin;
	};

	const clamped = Math.min(Math.max(300, 450), 530);

	return (
		<>
			{isMapOpen ? (
				<>
					<div className='mb-3 flex flex-col items-stretch gap-y-0 self-stretch'>
						<div className='w-full'>
							<LiveMap
								initLocation={user?.cityLocation?.location}
								// initLocation={(selectedCity || user?.cityLocation)?.location}
								positions={pin}
								// width={size.width > 860 ? 620 : size.width * 0.83}
								// width={clamped}
								height={200}
								onClick={(lat, lng) => changePin(lat, lng)}
								shouldAnimateTo={tempMapLocation}
								page='pin'
							/>
						</div>

						<div className='flex flex-row items-center gap-x-2'>
							<FloatingTextinput
								label='Οδός & αριθμός'
								value={mapAddress}
								onChange={e => setMapAddress(e)}
								onBlur={validateMapAddress}
								error={errorMapAddress}
								errorText='Η διεύθυνση δεν μπορεί να είναι κενή'
								white
								size={'full'}
							/>
						</div>
						<div className='mb-5 flex flex-row justify-evenly'>
							<Button
								styleStyle={{ marginTop: 0 }}
								onClick={onSaveMap}
								disabled={saveDisabledMap()}
								text={'Αποθήκευση'}
							/>
							<Button styleStyle={{ marginTop: 0 }} onClick={onBack} text={'Ακύρωση'} />
						</div>
					</div>
				</>
			) : (
				<div className='relative flex w-full flex-row items-center self-stretch'>
					<Autocomplete
						suggestions={suggestions}
						getResults={getResults}
						onChangeText={e => {
							// console.log('daaa', setAddress);
							// Typing is the signal that lookups are wanted. Only a real
							// keystroke reaches this — picking a suggestion and saving the
							// map both write the street through setAddress directly, so
							// they still cannot arm a query.
							disableSugRef.current = false;
							setAddress({ ...address, street: e, location: null });
						}}
						// searchOptions={searchOptions}
						value={address?.street}
						label={label || 'Address'}
						onBlur={validateAddress}
						onFocus={onFocus}
						error={errorAddress}
						errorText='Επίλεξε ένα από τα αποτελέσματα'
						size='full'
						{...(showDropper
							? {
									rightComponents: [
										{
											key: 'map-pin',
											onClick: openMap,
											component: (
												<div className='px-1'>
													<MapPinPlus size={24} color={colors.sky[500]} />
												</div>
											),
										},
									],
								}
							: {})}
						// error={errorName}
						// errorText="Επιλέξτε μία από τις πόλεις από το μενού"
					/>
					{/* {showDropper && (
						<div
							//  className='pt-4 ml-1.5 cursor-pointer'
							onClick={openMap}
							className='absolute top-5 right-3 z-100312 cursor-pointer p-0.5'
							// style=
						>
							<MapPinPen size={22} />
						</div>
					)} */}
				</div>
			)}
		</>
	);
};
